// @flow

// Libs
import React, { Component } from 'react'
import moment from 'moment'
import _isEmpty from 'lodash/isEmpty'
import _every from 'lodash/every'
import _find from 'lodash/find'
import _size from 'lodash/size'

// Components
import { ImportJobsTool } from 'hfty-import-jobs-tool-dom'
import ReopenJob from 'hfty-reopen-job-dom/src/components/ReopenJob'
import DataTable from './DataTable'
import Add from 'fc-icons/src/Add'
import Pencil from 'fc-icons/src/Pencil'
import Delete from 'fc-icons/src/Delete'
import Upload from 'fc-icons/src/Upload'
import AddSoilJob from './AddSoilJob'
import EditSoilJob from './EditSoilJob'
import DeleteSoilJobs from './DeleteSoilJobs'
import { PaymentStatusModal } from 'hfty-checkout'

// Operations
import { ImportJobsToolOperation } from 'hfty-operator-import-jobs-tool/types'
import { ImportJobsPopupOperation } from 'hfty-operator-import-jobs-popup/types'

// Selectors
import deleteOps$ from '../selectors/deleteOps'
import Select from 'react-select'
import { DateRangeSelector } from 'fc-components-datepicker'

// Container
import soilJobsManagerContainer from '../containers/SoilJobsManager'

// Styles
import { white, dark, lightGrey } from 'hfty-styles/src/colors'

// Constants
import { DATE_FORMAT } from './../constants'

// Types
import { ReopenJobOperation } from 'hfty-operator-reopen-job/types'

// Styles
const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
  },
  panel: {
    width: 300,
    backgroundColor: '#333',
    color: '#fff',
    boxShadow: '-2 0 2px rgba(0, 0, 0, 0.2)',
  },
  button: {
    cursor: 'pointer',
    padding: '2px 7px',
  },
  toolBarGroup: {
    display: 'flex',
    borderRadius: 2,
    alignItems: 'center',
    padding: '3px 7px',
    backgroundColor: white(),
    boxShadow: '0 0 2px rgba(0, 0, 0, 0.2)',
    marginLeft: 10,
  },
  select: {
    width: 150,
    marginRight: 5,
  },
  buttonLabels: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '2px',
    marginRight: '2px',
  },
}

// Types
type Props = {
  selectedIds: Array<number>,
  operation: SoilJobOperation,
  operations: Array<SoilJobOperation>,
  importJobsToolOperations: Array<ImportJobsToolOperation>,
  importJobsPopupOperations: Array<ImportJobsPopupOperation>,
  reopenJobOperation: Array<ReopenJobOperation>,
  authenticatedUser: Object,
  fieldSelectorOptions: Array,
  userSelectorOptions: Array,
  subfields: Array,
  jobFilters: Object,
  jobStatusOptions: Array,
  token: string,
  jobs: Array,
  history: Object,
  seasonSelectorOptions: Array<Object>,
  selectedSeasonId: number,
  actions: {
    onAdd: Function,
    onEdit: Function,
    onDelete: Function,
    showImportJobsTool: Function,
    onChangeOperation: Function,
    onCancelOperations: Function,
    onSubmitOperations: Function,
    onFilterBySeason: Function,
    onFilterByStatus: Function,
    onFilterByDateRange: Function,
    onFilterByAssignedTo: Function,
    onSubmitImportJobsToolOperations: Function,
    updateImportJobsToolOperation: Function,
    onCancelImportJobsToolOperations: Function,
    onCancelImportJobsPopupOperations: Function,
    onChangeReopenJobOperations: Function,
    onSubmitReopenJobOperations: Function,
    onCancelReopenJobOperations: Function,
  },
}

// Exports
export class SoilJobsManager extends Component {
  props: Props
  filterJobsBySeason: Function
  filterJobsByDateRange: Function
  filterJobsByStatus: Function
  filterJobsByAssignedTo: Function

  constructor(props: Props) {
    super(props)

    this.state = {
      isPaymentModalOpen: false,
    }

    this.filterJobsByDateRange = this.filterJobsByDateRange.bind(this)
    this.filterJobsByStatus = this.filterJobsByStatus.bind(this)
    this.filterJobsByAssignedTo = this.filterJobsByAssignedTo.bind(this)
    this.filterJobsBySeason = this.filterJobsBySeason.bind(this)
  }

  canEdit(): boolean {
    return this.props.selectedIds.length === 1
  }

  canDelete(): boolean {
    const jobs = this.props.jobs
    return (
      this.props.selectedIds.length > 0 &&
      _every(this.props.selectedIds, function (id: number): boolean {
        const job = _find(jobs, { id: id })
        return job.status !== 'Shipping' && job.status !== 'Results Posted'
      })
    )
  }

  canAdd(): boolean {
    return this.props.fieldSelectorOptions.length > 0
  }

  showExtraWarning(): boolean {
    const jobs = this.props.jobs
    const retVal = _find(this.props.selectedIds, function (id: number): boolean {
      const job = _find(jobs, { id: id })
      return job.status === 'Sampling' || job.status === 'Order Test'
    })
    return retVal
  }

  filterJobsByAssignedTo(e: Object) {
    this.props.actions.onFilterByAssignedTo((e && e.value) || '')
  }

  filterJobsBySeason(e: Object) {
    this.props.actions.onFilterBySeason((e && e.value) || '')
  }

  filterJobsByDateRange({ start, end }: Object) {
    this.props.actions.onFilterByDateRange({
      startDate: start ? moment(start).format(DATE_FORMAT) : '',
      endDate: end ? moment(end).format(DATE_FORMAT) : '',
    })
  }

  filterJobsByStatus(e: Object) {
    this.props.actions.onFilterByStatus((e && e.value) || '')
  }

  componentDidMount() {
    const { history } = this.props

    if (history && history.location && history.location.pathname === '/dashboard/review-payment-status') {
      this.setState({ isPaymentModalOpen: true })
    }
  }

  render(): React.Element<any> {
    const { jobFilters, selectedSeasonId } = this.props

    return (
      <div data-test="fc-soil-jobs-dom/SoilJobsManager" style={styles.container}>
        <DataTable token={this.props.token} actions={this.props.actions}>
          <div style={styles.toolBarGroup}>
            <Select
              name="season-selector"
              placeholder={'Season'}
              value={jobFilters.season || selectedSeasonId}
              disabled={!_size(this.props.seasonSelectorOptions)}
              options={this.props.seasonSelectorOptions}
              onChange={this.filterJobsBySeason}
              className="clear-margin"
              style={styles.select}
            />
          </div>
          <div style={styles.toolBarGroup}>
            <Select
              id="assignedTo.select"
              name="assignedTo-selector"
              placeholder={'Assigned To'}
              value={jobFilters.assignTo}
              options={this.props.userSelectorOptions}
              onChange={this.filterJobsByAssignedTo}
              style={styles.select}
            />
          </div>

          <div style={styles.toolBarGroup}>
            <Select
              id="status.select"
              name="status-selector"
              placeholder={'Status'}
              value={jobFilters.status}
              options={this.props.jobStatusOptions}
              onChange={this.filterJobsByStatus}
              style={styles.select}
            />
          </div>

          <div style={styles.toolBarGroup}>
            <DateRangeSelector
              onChange={this.filterJobsByDateRange}
              start={!_isEmpty(jobFilters.startDate) ? moment(jobFilters.startDate, DATE_FORMAT) : null}
              end={!_isEmpty(jobFilters.endDate) ? moment(jobFilters.endDate, DATE_FORMAT) : null}
              defaultValueStart={moment().subtract(1, 'month')}
              defaultValueEnd={null}
              hideLabel
              maxLimit={moment()}
            />
          </div>

          <div style={styles.toolBarGroup}>
            <div
              style={styles.button}
              title={'Add Job'}
              onClick={this.canAdd() ? (): Function => this.props.actions.onAdd(this.props.authenticatedUser.id) : null}
            >
              <Add color={this.canAdd() ? dark() : lightGrey()} />
            </div>
            <div
              style={styles.button}
              title={'Import Jobs'}
              onClick={(): Function => this.props.actions.showImportJobsTool()}
            >
              <Upload />
            </div>
            <div style={styles.button} title={'Edit Job'} onClick={this.canEdit() ? this.props.actions.onEdit : null}>
              <Pencil color={this.canEdit() ? dark() : lightGrey()} />
            </div>
            <div style={styles.button} onClick={this.canDelete() ? this.props.actions.onDelete : null}>
              <Delete color={this.canDelete() ? dark() : lightGrey()} />
            </div>
          </div>
        </DataTable>
        <AddSoilJob
          operation={this.props.operation}
          fieldSelectorOptions={this.props.fieldSelectorOptions}
          userSelectorOptions={this.props.userSelectorOptions}
          subfields={this.props.subfields}
          onChange={this.props.actions.onChangeOperation}
          onSubmit={this.props.actions.onSubmitOperations}
          onCancel={this.props.actions.onCancelOperations}
        />
        <EditSoilJob
          operation={this.props.operation}
          fieldSelectorOptions={this.props.fieldSelectorOptions}
          userSelectorOptions={this.props.userSelectorOptions}
          jobStatusOptions={this.props.jobStatusOptions}
          subfields={this.props.subfields}
          selectedIds={this.props.selectedIds}
          onChange={this.props.actions.onChangeOperation}
          onSubmit={this.props.actions.onSubmitOperations}
          onCancel={this.props.actions.onCancelOperations}
        />
        <DeleteSoilJobs
          operations={deleteOps$(this.props.operations)}
          onSubmit={this.props.actions.onSubmitOperations}
          onCancel={this.props.actions.onCancelOperations}
          showExtraWarning={this.showExtraWarning()}
        />
        <ImportJobsTool
          importJobsToolOperations={this.props.importJobsToolOperations}
          importJobsPopupOperations={this.props.importJobsPopupOperations}
          seasonOptions={this.props.seasonSelectorOptions}
          onSubmit={this.props.actions.onSubmitImportJobsToolOperations}
          updateImportJobsToolOperation={this.props.actions.updateImportJobsToolOperation}
          onCancel={this.props.actions.onCancelImportJobsToolOperations}
          closePopup={this.props.actions.onCancelImportJobsPopupOperations}
        />
        {this.props.reopenJobOperation && (
          <ReopenJob
            operation={this.props.reopenJobOperation}
            subfields={this.props.subfields}
            fieldSelectorOptions={this.props.fieldSelectorOptions}
            userSelectorOptions={this.props.userSelectorOptions}
            jobStatusOptions={this.props.jobStatusOptions}
            onChange={this.props.actions.onChangeReopenJobOperations}
            onSubmit={this.props.actions.onSubmitReopenJobOperations}
            onCancel={this.props.actions.onCancelReopenJobOperations}
          />
        )}
        <PaymentStatusModal
          open={this.state.isPaymentModalOpen}
          token={this.props.token}
          history={this.props.history}
          onClose={() => {
            this.setState({ isPaymentModalOpen: false })
          }}
        />
      </div>
    )
  }
}

export default soilJobsManagerContainer(SoilJobsManager)
